import type { MetricChartDataRow } from '@/shared/components/data-visualization/recharts';
import {
    balanceSheetLineItemOrdering,
    cashFlowLineItemOrdering,
    incomeStatementLineItemOrdering,
    multiplesLineItemOrdering,
} from '@/shared/schemas';
import type { FinancialStatementPeriod, SavedSeriesDefinition } from '@/types';
import { dayjs, FundamentalValuationsType } from '@/utilities';

export type { FinancialStatementDefaults, FinancialStatementPeriod } from '@/types/savedSeries';

export type FinancialSeriesSnapshot = SavedSeriesDefinition;

export interface FinancialAnalysisRequestContext {
    endDate: string;
    limit: number;
    period: FinancialStatementPeriod;
    startDate: string;
}

export type FinancialAnalysisRequestParams = Record<string, number | string>;

export const dividendMetricTypes: Partial<Record<string, FundamentalValuationsType>> = {
    amount: FundamentalValuationsType.VALUE,
    percentChange: FundamentalValuationsType.PERCENT,
    splitAdjustedAmount: FundamentalValuationsType.VALUE,
};

export const financialStatementMetricTypes: Partial<Record<string, FundamentalValuationsType>> = {
    numberOfShares: FundamentalValuationsType.NUMBER,
    sharesOutstanding: FundamentalValuationsType.NUMBER,
    weightedAverageShares: FundamentalValuationsType.NUMBER,
    weightedAverageSharesDiluted: FundamentalValuationsType.NUMBER,
    weightedAverageSharesOutstanding: FundamentalValuationsType.NUMBER,
    weightedAverageSharesOutstandingDiluted: FundamentalValuationsType.NUMBER,
    weightedAverageShsOut: FundamentalValuationsType.NUMBER,
    weightedAverageShsOutDil: FundamentalValuationsType.NUMBER,
};

const firstString = (...values: unknown[]) =>
    values.find((value): value is string => typeof value === 'string' && value.trim().length > 0);

const firstOptionalFiniteNumber = (...values: unknown[]): number | null => {
    for (const value of values) {
        if (value === null || value === undefined || value === '') {
            continue;
        }

        const numberValue = typeof value === 'number' ? value : Number(value);

        if (Number.isFinite(numberValue)) {
            return numberValue;
        }
    }

    return null;
};

const firstFiniteNumber = (...values: unknown[]) => firstOptionalFiniteNumber(...values) ?? 0;

const firstValidDate = (...values: unknown[]): string | undefined => {
    const value = firstString(...values);
    const parsedDate = dayjs(value);

    return value && parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : undefined;
};

export interface DividendHistoryRow {
    adjustedDividend: number | null;
    declarationDate?: string;
    dividend: number | null;
    exDividendDate: string;
    frequency?: string;
    key: string;
    paymentDate?: string;
    recordDate?: string;
    symbol?: string;
    yield: number | null;
}

export const isDividendHistoryRowLocked = (
    row: DividendHistoryRow,
    historicalDataYears: number | 'unlimited',
    referenceDate: string = dayjs().format('YYYY-MM-DD'),
): boolean =>
    historicalDataYears !== 'unlimited' &&
    dayjs(row.exDividendDate).isBefore(dayjs(referenceDate).subtract(historicalDataYears, 'year'), 'day');

/** Preserves one table row per FMP dividend event while normalizing provider aliases. */
export const normalizeDividendHistoryRows = (rows: Array<Record<string, unknown>>): DividendHistoryRow[] =>
    rows
        .map((row, index): DividendHistoryRow | undefined => {
            const exDividendDate = firstValidDate(row.exDividendDate, row.exDate, row.date);

            if (!exDividendDate) {
                return undefined;
            }

            const symbol = firstString(row.symbol)?.toUpperCase();
            const declarationDate = firstValidDate(row.declarationDate);
            const paymentDate = firstValidDate(row.paymentDate, row.payDate);
            const recordDate = firstValidDate(row.recordDate);

            return {
                adjustedDividend: firstOptionalFiniteNumber(
                    row.splitAdjustedAmount,
                    row.adjDividend,
                    row.adjustedDividend,
                    row.amount,
                    row.dividend,
                ),
                declarationDate,
                dividend: firstOptionalFiniteNumber(row.amount, row.dividend),
                exDividendDate,
                frequency: firstString(row.frequency),
                key: [
                    symbol ?? 'dividend',
                    exDividendDate,
                    paymentDate ?? recordDate ?? declarationDate ?? 'event',
                    index,
                ]
                    .join(':')
                    .replace(/\s+/gu, '-'),
                paymentDate,
                recordDate,
                symbol,
                yield: firstOptionalFiniteNumber(row.yield),
            };
        })
        .filter((row): row is DividendHistoryRow => Boolean(row))
        .sort((left, right) => dayjs(right.exDividendDate).valueOf() - dayjs(left.exDividendDate).valueOf());

export const normalizeDividendAnalysisRows = (
    rows: Array<Record<string, unknown>>,
    period: FinancialStatementPeriod,
): MetricChartDataRow[] => {
    const groupedRows = new Map<string, MetricChartDataRow>();

    rows.forEach((row) => {
        const exDividendDate = firstString(row.exDividendDate, row.exDate, row.date);
        const date = dayjs(exDividendDate);

        if (!exDividendDate || !date.isValid()) {
            return;
        }

        const year = date.year();
        const quarter = date.quarter();
        const bucketKey = period === 'annual' ? String(year) : `${year}-Q${quarter}`;
        const amount = firstFiniteNumber(row.amount, row.dividend);
        const splitAdjustedAmount = firstFiniteNumber(
            row.splitAdjustedAmount,
            row.adjDividend,
            row.adjustedDividend,
            amount,
        );
        const current = groupedRows.get(bucketKey);

        groupedRows.set(bucketKey, {
            ...current,
            amount: Number(current?.amount ?? 0) + amount,
            calendarYear: String(year),
            date:
                period === 'annual'
                    ? date.endOf('year').format('YYYY-MM-DD')
                    : date.endOf('quarter').format('YYYY-MM-DD'),
            periodLabel: period === 'annual' ? String(year) : `Q${quarter} ${year}`,
            splitAdjustedAmount: Number(current?.splitAdjustedAmount ?? 0) + splitAdjustedAmount,
        });
    });

    const sortedRows = Array.from(groupedRows.values()).sort(
        (left, right) => dayjs(left.date).valueOf() - dayjs(right.date).valueOf(),
    );

    return sortedRows.map((row, index) => {
        const previousAmount = sortedRows[index - 1]?.splitAdjustedAmount;
        const currentAmount = row.splitAdjustedAmount;
        const hasComparableAmounts =
            typeof previousAmount === 'number' &&
            previousAmount !== 0 &&
            typeof currentAmount === 'number' &&
            Number.isFinite(currentAmount);

        return {
            ...row,
            percentChange: hasComparableAmounts ? (currentAmount - previousAmount) / previousAmount : null,
        };
    });
};

export const getDividendAnalysisRequestParams = ({
    endDate,
    limit,
    period,
    startDate,
}: FinancialAnalysisRequestContext): FinancialAnalysisRequestParams => ({
    from: startDate,
    limit: period === 'annual' ? limit * 4 + 4 : limit * 2 + 8,
    to: endDate,
});

interface ResolveFinancialSeriesLineItemsArgs {
    available: string[];
    current: string[];
    fallback?: string;
    requested: string[];
}

export const resolveFirstAvailableFinancialLineItem = (available: string[], candidates: string[]) => {
    const availableLineItems = new Set(available);

    return candidates.find((lineItem) => availableLineItems.has(lineItem));
};

export const resolveFinancialSeriesLineItems = ({
    available,
    current,
    fallback,
    requested,
}: ResolveFinancialSeriesLineItemsArgs): string[] => {
    const availableLineItems = new Set(available);
    const validCurrent = current.filter((lineItem) => availableLineItems.has(lineItem)).slice(0, 5);

    if (validCurrent.length) {
        return validCurrent;
    }

    const validRequested = requested.filter((lineItem) => availableLineItems.has(lineItem)).slice(0, 5);

    if (validRequested.length) {
        return validRequested;
    }

    return fallback && availableLineItems.has(fallback) ? [fallback] : [];
};

export const FINANCIAL_STATEMENT_SECTIONS = [
    {
        key: 'income',
        label: 'Income Statement',
        lineItems: incomeStatementLineItemOrdering,
    },
    {
        key: 'cashFlows',
        label: 'Cash Flows',
        lineItems: cashFlowLineItemOrdering,
    },
    {
        key: 'balanceSheet',
        label: 'Balance Sheet',
        lineItems: balanceSheetLineItemOrdering,
    },
    {
        key: 'multiples',
        label: 'Multiples',
        lineItems: multiplesLineItemOrdering,
    },
] as const;

export const financialStatementSectionLabel = (sectionKey: string) =>
    FINANCIAL_STATEMENT_SECTIONS.find((section) => section.key === sectionKey)?.label ?? 'Financial Series';
